import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Row, Col, Typography, Space } from 'antd';
import {
  HeartOutlined,
  SafetyOutlined,
  TeamOutlined,
  CalendarOutlined,
  ThunderboltOutlined,
  FireOutlined,
  CloudOutlined,
  EnvironmentOutlined,
  StarOutlined
} from '@ant-design/icons';
import { useTheme } from '../components/ParticleBackground';
import MouseEffectCard from '../components/ui/mouse-effect-card';
import useResponsive from '../hooks/useResponsive';

const { Title, Paragraph } = Typography;

// Scroll-triggered animation hook
const useScrollAnimation = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return scrollY;
};

// Intersection Observer hook for reveal animations
const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [threshold]);

  return [ref, inView];
};

// Counter animation component
const AnimatedCounter = ({ end, duration = 2000, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const [ref, inView] = useInView(0.3);

  useEffect(() => {
    if (inView && !hasAnimated) {
      setHasAnimated(true);
      let startTime;
      let animationFrame;

      const animate = (timestamp) => {
        if (!startTime) startTime = timestamp;
        const progress = timestamp - startTime;
        const percentage = Math.min(progress / duration, 1);

        // Easing function for smooth animation
        const easeOutQuart = 1 - Math.pow(1 - percentage, 4);
        setCount(Math.floor(end * easeOutQuart));

        if (percentage < 1) {
          animationFrame = requestAnimationFrame(animate);
        }
      };

      animationFrame = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(animationFrame);
    }
  }, [inView, hasAnimated, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
};

const TherapyContent = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const { isMobile } = useResponsive();
  const scrollY = useScrollAnimation();

  const [heroRef, heroInView] = useInView(0.1);
  const [elementsRef, elementsInView] = useInView(0.2);
  const [servicesRef, servicesInView] = useInView(0.2);
  const [storyRef, storyInView] = useInView(0.2);
  const [statsRef, statsInView] = useInView(0.3);
  const [ctaRef, ctaInView] = useInView(0.2);

  const getThemeStyles = () => {
    if (theme === 'dark') {
      return {
        background: 'rgba(15, 23, 42, 0.95)',
        titleColor: '#f8fafc',
        textColor: '#cbd5e1',
        cardBg: 'rgba(30, 41, 59, 0.8)',
        cardBorder: 'rgba(71, 85, 105, 0.3)',
        ctaBg: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        ctaTextColor: '#f8fafc',
        accentColor: '#60a5fa',
        gradient1: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        elementBg: 'rgba(30, 41, 59, 0.6)'
      };
    } else {
      return {
        background: 'rgba(255, 255, 255, 0.95)',
        titleColor: '#1f2937',
        textColor: '#4b5563',
        cardBg: 'rgba(255, 255, 255, 0.9)',
        cardBorder: 'rgba(226, 232, 240, 0.6)',
        ctaBg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        ctaTextColor: 'white',
        accentColor: '#2563eb',
        gradient1: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        gradient2: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
        gradient3: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        elementBg: 'rgba(255, 255, 255, 0.7)'
      };
    }
  };

  const themeStyles = getThemeStyles();

  // Five Elements with icons and colors
  const elements = [
    {
      icon: <EnvironmentOutlined />,
      title: 'Earth',
      sanskrit: 'Prithvi',
      description: 'Grounding, stability, and nourishment for your physical body',
      color: '#92400e',
      gradient: 'linear-gradient(135deg, #d97706 0%, #92400e 100%)'
    },
    {
      icon: <CloudOutlined />,
      title: 'Water',
      sanskrit: 'Jala',
      description: 'Flow, adaptability, and emotional balance',
      color: '#0c4a6e',
      gradient: 'linear-gradient(135deg, #0ea5e9 0%, #0c4a6e 100%)'
    },
    {
      icon: <FireOutlined />,
      title: 'Fire',
      sanskrit: 'Agni',
      description: 'Transformation, energy, and digestive power',
      color: '#991b1b',
      gradient: 'linear-gradient(135deg, #f97316 0%, #991b1b 100%)'
    },
    {
      icon: <ThunderboltOutlined />,
      title: 'Air',
      sanskrit: 'Vayu',
      description: 'Movement, breath, and life force',
      color: '#065f46',
      gradient: 'linear-gradient(135deg, #10b981 0%, #065f46 100%)'
    },
    {
      icon: <StarOutlined />,
      title: 'Ether',
      sanskrit: 'Akasha',
      description: 'Space, consciousness, and spiritual connection',
      color: '#4c1d95',
      gradient: 'linear-gradient(135deg, #a78bfa 0%, #4c1d95 100%)'
    }
  ];

  const services = [
    {
      icon: <HeartOutlined style={{ fontSize: '48px', color: '#f43f5e' }} />,
      title: "Women's Health",
      description: 'Specialized naturopathic care for hormonal balance, PCOS, and reproductive health',
      gradient: 'linear-gradient(135deg, #fda4af 0%, #f43f5e 100%)'
    },
    {
      icon: <TeamOutlined style={{ fontSize: '48px', color: '#ec4899' }} />,
      title: 'Pre & Postnatal Care',
      description: 'Holistic support through pregnancy and motherhood with yoga and naturopathy',
      gradient: 'linear-gradient(135deg, #f9a8d4 0%, #ec4899 100%)'
    },
    {
      icon: <CalendarOutlined style={{ fontSize: '48px', color: '#8b5cf6' }} />,
      title: 'Garbhasanskar',
      description: 'Ancient prenatal education for nurturing both mother and baby',
      gradient: 'linear-gradient(135deg, #c4b5fd 0%, #8b5cf6 100%)'
    },
    {
      icon: <SafetyOutlined style={{ fontSize: '48px', color: '#10b981' }} />,
      title: 'Lifestyle Medicine',
      description: 'Personalized guidance for sustainable, nature-aligned living',
      gradient: 'linear-gradient(135deg, #6ee7b7 0%, #10b981 100%)'
    }
  ];

  return (
    <div style={{
      background: theme === 'dark'
        ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.95) 0%, rgba(30, 41, 59, 0.95) 100%)'
        : 'linear-gradient(180deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>

      {/* Animated Background Elements */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 0
      }}>
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            style={{
              position: 'absolute',
              width: Math.random() * 300 + 50 + 'px',
              height: Math.random() * 300 + 50 + 'px',
              borderRadius: '50%',
              background: `radial-gradient(circle, ${theme === 'dark' ? 'rgba(102, 126, 234, 0.05)' : 'rgba(102, 126, 234, 0.03)'} 0%, transparent 70%)`,
              top: Math.random() * 100 + '%',
              left: Math.random() * 100 + '%',
              animation: `float ${Math.random() * 20 + 10}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 5}s`,
              transform: `translateY(${scrollY * (i % 2 === 0 ? 0.1 : -0.1)}px)`
            }}
          />
        ))}
      </div>

      <div style={{
        maxWidth: '1400px',
        margin: '0 auto',
        padding: '0 24px',
        position: 'relative',
        zIndex: 1
      }}>

        {/* MouseEffectCard Hero Section */}
        <div
          ref={heroRef}
          style={{
            display: 'flex',
            justifyContent: 'center',
            padding: isMobile ? '32px 0' : '80px 0',
            opacity: heroInView ? 1 : 0,
            transform: heroInView ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)'
          }}
        >
          <MouseEffectCard
            theme={theme}
            title="EkahHealth"
            subtitle="United by one powerful belief: Your body heals itself. We are dedicated naturopaths supporting evidence-based, sustainable living."
            primaryCtaText="Explore Services"
            secondaryCtaText="Learn More"
            footerText="Natural • Holistic • Evidence-Based"
            onPrimaryClick={() => navigate('/services')}
            onSecondaryClick={() => navigate('/about')}
            style={{
              background: theme === 'dark'
                ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)'
                : 'linear-gradient(135deg, rgba(255, 255, 255, 0.9) 0%, rgba(249, 250, 251, 0.95) 100%)',
              backdropFilter: 'blur(20px)',
            }}
          />
        </div>


        {/* Services Section */}
        <div
          ref={servicesRef}
          style={{
            padding: isMobile ? '32px 0' : '80px 0',
            position: 'relative'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: '60px' }}>
            <Title
              level={2}
              style={{
                fontSize: 'clamp(2rem, 5vw, 3.5rem)',
                fontWeight: '700',
                color: themeStyles.titleColor,
                marginBottom: isMobile ? '12px' : '16px'
              }}
            >
              Our Specialized Services
            </Title>
            <Paragraph style={{
              fontSize: 'clamp(16px, 2vw, 20px)',
              color: themeStyles.textColor,
              maxWidth: '700px',
              margin: '0 auto'
            }}>
              Holistic care tailored to your unique journey
            </Paragraph>
          </div>

          <Row gutter={isMobile ? [16, 16] : [32, 32]}>
            {services.map((service, index) => (
              <Col xs={24} sm={12} lg={6} key={index} style={{
                opacity: servicesInView ? 1 : 0,
                transform: servicesInView ? 'translateY(0)' : 'translateY(60px)',
                transition: `all 0.8s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s`
              }}>
                <Card
                  style={{
                    height: '100%',
                    textAlign: 'center',
                    background: themeStyles.cardBg,
                    border: 'none',
                    borderRadius: '24px',
                    backdropFilter: 'blur(20px)',
                    boxShadow: theme === 'dark'
                      ? '0 10px 40px rgba(0, 0, 0, 0.3)'
                      : '0 10px 40px rgba(0, 0, 0, 0.08)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    overflow: 'hidden'
                  }}
                  styles={{ body: { padding: '40px 28px' } }}
                  hoverable
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-12px)';
                    e.currentTarget.style.boxShadow = theme === 'dark'
                      ? '0 20px 60px rgba(0, 0, 0, 0.4)'
                      : '0 20px 60px rgba(0, 0, 0, 0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = theme === 'dark'
                      ? '0 10px 40px rgba(0, 0, 0, 0.3)'
                      : '0 10px 40px rgba(0, 0, 0, 0.08)';
                  }}
                >
                  <div style={{ marginBottom: '24px' }}>{service.icon}</div>
                  <Title level={4} style={{
                    color: themeStyles.titleColor,
                    marginBottom: '16px',
                    fontSize: 'clamp(18px, 2vw, 20px)',
                    fontWeight: '700'
                  }}>
                    {service.title}
                  </Title>
                  <Paragraph style={{
                    color: themeStyles.textColor,
                    margin: 0,
                    fontSize: '15px',
                    lineHeight: '1.6'
                  }}>
                    {service.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>

          <div style={{ textAlign: 'center', marginTop: '48px' }}>
            <Button
              type="primary"
              size="large"
              style={{
                padding: '16px 48px',
                height: 'auto',
                fontSize: '18px',
                fontWeight: '600',
                borderRadius: '12px',
                background: themeStyles.gradient1,
                border: 'none',
                boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                transition: 'all 0.3s ease'
              }}
              onClick={() => navigate('/services')}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.3)';
              }}
            >
              Explore All Services
            </Button>
          </div>
        </div>

        {/* Story Section */}
        <div
          ref={storyRef}
          style={{
            padding: isMobile ? '32px 0' : '80px 0',
            position: 'relative'
          }}
        >
          <Row gutter={isMobile ? [24, 24] : [48, 48]} align="middle">
            <Col xs={24} lg={12} style={{
              opacity: storyInView ? 1 : 0,
              transform: storyInView ? 'translateX(0)' : 'translateX(-60px)',
              transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <div style={{
                background: themeStyles.cardBg,
                borderRadius: '24px',
                padding: isMobile ? '24px' : '48px',
                backdropFilter: 'blur(20px)',
                border: `1px solid ${themeStyles.cardBorder}`,
                boxShadow: theme === 'dark'
                  ? '0 20px 60px rgba(0, 0, 0, 0.3)'
                  : '0 20px 60px rgba(0, 0, 0, 0.1)'
              }}>
                <Title level={2} style={{
                  color: themeStyles.titleColor,
                  marginBottom: '24px',
                  fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
                  fontWeight: '700'
                }}>
                  The Cure Within
                </Title>
                <Paragraph style={{
                  color: themeStyles.textColor,
                  fontSize: 'clamp(15px, 2vw, 18px)',
                  lineHeight: '1.8',
                  marginBottom: '20px'
                }}>
                  My journey through hormonal imbalance and PCOS taught me that no pill could heal
                  the disconnect between body and mind. It was a case of <em>Adhija Vyadhi</em>—a
                  disease born from separation.
                </Paragraph>
                <Paragraph style={{
                  color: themeStyles.textColor,
                  fontSize: 'clamp(15px, 2vw, 18px)',
                  lineHeight: '1.8',
                  marginBottom: '20px'
                }}>
                  As a Naturopathy student, I discovered the truth: when I aligned my diet, actions,
                  and lifestyle with nature's wisdom, my health transformed completely and naturally.
                </Paragraph>
                <Paragraph style={{
                  color: themeStyles.titleColor,
                  fontSize: 'clamp(16px, 2vw, 20px)',
                  lineHeight: '1.8',
                  fontWeight: '600',
                  fontStyle: 'italic',
                  padding: '24px',
                  background: theme === 'dark'
                    ? 'rgba(102, 126, 234, 0.1)'
                    : 'rgba(102, 126, 234, 0.05)',
                  borderRadius: '16px',
                  borderLeft: `4px solid ${themeStyles.accentColor}`
                }}>
                  "The cure is in you; it will come from within."
                </Paragraph>
              </div>
            </Col>
            <Col xs={24} lg={12} style={{
              opacity: storyInView ? 1 : 0,
              transform: storyInView ? 'translateX(0)' : 'translateX(60px)',
              transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1) 0.2s'
            }}>
              <div style={{
                position: 'relative',
                borderRadius: '24px',
                overflow: 'hidden',
                height: '500px',
                boxShadow: theme === 'dark'
                  ? '0 20px 60px rgba(0, 0, 0, 0.4)'
                  : '0 20px 60px rgba(0, 0, 0, 0.15)'
              }}>
                <img
                  src="https://images.unsplash.com/photo-1506126613408-eca07ce68773?q=80&w=800&auto=format&fit=crop"
                  alt="Natural healing"
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover'
                  }}
                />
                <div style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(45deg, rgba(102, 126, 234, 0.2), rgba(118, 75, 162, 0.2))'
                }} />
              </div>
            </Col>
          </Row>
        </div>


        {/* Final CTA Section */}
        <div
          ref={ctaRef}
          style={{
            padding: isMobile ? '32px 0 48px' : '80px 0 100px',
            textAlign: 'center',
            position: 'relative'
          }}
        >
          <div style={{
            background: themeStyles.cardBg,
            borderRadius: '32px',
            padding: isMobile ? '32px 24px' : '64px 48px',
            backdropFilter: 'blur(20px)',
            border: `1px solid ${themeStyles.cardBorder}`,
            boxShadow: theme === 'dark'
              ? '0 20px 60px rgba(0, 0, 0, 0.3)'
              : '0 20px 60px rgba(0, 0, 0, 0.1)',
            opacity: ctaInView ? 1 : 0,
            transform: ctaInView ? 'translateY(0)' : 'translateY(40px)',
            transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)'
          }}>
            <Title level={2} style={{
              color: themeStyles.titleColor,
              marginBottom: '24px',
              fontSize: 'clamp(2rem, 5vw, 3rem)',
              fontWeight: '700'
            }}>
              Begin Your Healing Journey
            </Title>
            <Paragraph style={{
              fontSize: 'clamp(16px, 2vw, 20px)',
              color: themeStyles.textColor,
              marginBottom: '40px',
              maxWidth: '700px',
              margin: '0 auto 40px',
              lineHeight: '1.7'
            }}>
              Your body knows the way to perfect health. We're here to help you reconnect
              with your natural healing power through the wisdom of naturopathy and yoga.
            </Paragraph>
            <Space size="large" wrap>
              <Button
                type="primary"
                size="large"
                style={{
                  padding: '18px 48px',
                  height: 'auto',
                  fontSize: '18px',
                  fontWeight: '600',
                  borderRadius: '12px',
                  background: themeStyles.gradient1,
                  border: 'none',
                  boxShadow: '0 8px 24px rgba(102, 126, 234, 0.3)',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => navigate('/booking')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(102, 126, 234, 0.4)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0) scale(1)';
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(102, 126, 234, 0.3)';
                }}
              >
                Book Your Consultation
              </Button>
              <Button
                size="large"
                style={{
                  padding: '18px 48px',
                  height: 'auto',
                  fontSize: '18px',
                  fontWeight: '600',
                  borderRadius: '12px',
                  borderColor: themeStyles.accentColor,
                  color: themeStyles.accentColor,
                  background: 'transparent',
                  transition: 'all 0.3s ease'
                }}
                onClick={() => navigate('/about')}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-4px)';
                  e.currentTarget.style.background = themeStyles.accentColor;
                  e.currentTarget.style.color = 'white';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = themeStyles.accentColor;
                }}
              >
                Learn Our Story
              </Button>
            </Space>
          </div>
        </div>

      </div>

      {/* Add keyframes for floating animation */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-20px) rotate(5deg); }
        }
      `}</style>
    </div>
  );
};

const WelcomeNew = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{ minHeight: '100vh' }}>
      <TherapyContent />
    </div>
  );
};

export default WelcomeNew;
